import { useEffect, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link } from "react-router";
import {
  AlertCircleIcon,
  ArrowLeftRightIcon,
  CheckIcon,
  ExternalLinkIcon,
  Link2Icon,
  LogOutIcon,
  MapPinIcon,
  RefreshCwIcon,
  UnlinkIcon,
  XIcon,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProgressDots } from "@/components/ui/progress-dots";
import { cn } from "@/lib/utils";
import { focusRing } from "@/lib/format";
import type {
  PlazaVeaCoverageDistrict,
  StoreDeliveryLocation,
  StoreStatus,
  WongSavedAddress,
} from "@/lib/api";
import { STORE_META, type StoreId } from "@/lib/stores";

export type SheetConnectionState =
  | "app-auth-missing"
  | "configured-missing"
  | "disconnected"
  | "connected-no-address"
  | "connected-with-address";

interface StoreSheetProps {
  store: StoreId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionState: SheetConnectionState;
  status: StoreStatus | null;
  // App-auth-missing handler
  onAppLogout: () => void;
  // Connect form
  connectEmail: string;
  connectPassword: string;
  onConnectEmailChange: (value: string) => void;
  onConnectPasswordChange: (value: string) => void;
  onConnectSubmit: () => Promise<void>;
  connectPending: boolean;
  connectError: string | null;
  // Disconnect — the actual confirm dialog is lifted to the parent (see
  // DisconnectConfirmDialog below) so it can render as a sibling of this
  // sheet rather than a nested dialog (illegal Radix markup).
  onRequestDisconnect: () => void;
  disconnectPending: boolean;
  // Address
  addressLoading: boolean;
  addressError: string | null;
  onRetryAddress: () => void;
  addressMode: "view" | "edit";
  onStartEditingAddress: () => void;
  onCancelEditingAddress: () => void;
  location: StoreDeliveryLocation | null;
  // Wong: pick one of the shopper's saved Wong addresses.
  wongAddresses: WongSavedAddress[];
  // The saved address the app currently delivers to (null until chosen).
  selectedWongAddressId: string | null;
  selectingWongAddressId: string | null;
  onSelectWongAddress: (addressId: string) => void;
  onRefreshWongAddresses: () => void;
  refreshingWongAddresses: boolean;
  // PlazaVea address edit form bindings
  street: string;
  number: string;
  districtQuery: string;
  selectedDistrictUbigeo: string;
  coverageDistricts: PlazaVeaCoverageDistrict[];
  filteredCoverageDistricts: PlazaVeaCoverageDistrict[];
  coverageLoading: boolean;
  districtAutocompleteOpen: boolean;
  highlightedDistrictIndex: number | null;
  onStreetChange: (value: string) => void;
  onNumberChange: (value: string) => void;
  onDistrictQueryChange: (value: string) => void;
  onSelectDistrict: (district: PlazaVeaCoverageDistrict) => void;
  onFocusDistrict: () => void;
  onBlurDistrict: () => void;
  onDistrictKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onSetHighlightedDistrictIndex: (index: number | null) => void;
  onSaveAddress: () => Promise<void>;
  onUseCurrentLocation: () => Promise<void>;
  saveAddressPending: boolean;
  geoResolving: boolean;
  locationError: string | null;
  activeDistrictOptionId: string | undefined;
}

export function StoreSheet(props: StoreSheetProps) {
  const { open, onOpenChange, connectionState, store } = props;
  const storeLabel = STORE_META[store].label;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Settings sheet, not a confirmation. Uses Dialog primitive so the
        // user can dismiss by tapping the overlay.
        aria-label={`Ajustes de ${storeLabel}`}
        className={cn(
          "flex flex-col gap-0 p-0 max-h-[95vh] sm:max-h-[95vh]",
          "fixed inset-x-0 bottom-0 top-auto w-full max-w-none translate-x-0 translate-y-0 rounded-t-2xl border-t border-x border-b-0",
          "sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border-b",
        )}
      >
        <header className="flex shrink-0 items-center justify-between gap-2 border-b px-5 py-4">
          <div className="min-w-0">
            <DialogTitle className="text-base font-semibold">{storeLabel}</DialogTitle>
            <DialogDescription>
              Conexión y dirección de entrega
            </DialogDescription>
          </div>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Cerrar"
              className={cn(
                "grid size-10 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted",
                focusRing,
              )}
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {connectionState === "app-auth-missing" && (
            <AppAuthMissingSection onLogout={props.onAppLogout} />
          )}
          {connectionState === "configured-missing" && <ConfiguredMissingSection storeLabel={storeLabel} />}
          {connectionState === "disconnected" && (
            <ConnectFormSection
              store={store}
              email={props.connectEmail}
              password={props.connectPassword}
              onEmailChange={props.onConnectEmailChange}
              onPasswordChange={props.onConnectPasswordChange}
              onSubmit={props.onConnectSubmit}
              pending={props.connectPending}
              error={props.connectError}
            />
          )}
          {(connectionState === "connected-no-address" || connectionState === "connected-with-address") && (
            <>
              <AccountBlock
                email={props.status?.email ?? null}
                pending={props.disconnectPending}
                onRequestDisconnect={props.onRequestDisconnect}
              />
              {store === "wong" ? <WongAddressSection {...props} /> : <AddressSection {...props} />}
            </>
          )}
        </div>

        <footer className="flex shrink-0 flex-col-reverse items-stretch gap-3 border-t px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/preferencias"
            onClick={() => onOpenChange(false)}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-sm text-sm text-muted-foreground hover:text-foreground",
              focusRing,
            )}
          >
            <ArrowLeftRightIcon className="size-4" />
            Cambiar supermercado
          </Link>
          <Button size="lg" className="h-11 w-full sm:w-auto" onClick={() => onOpenChange(false)}>
            Listo
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

interface DisconnectConfirmDialogProps {
  store: StoreId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  pending: boolean;
}

// Sibling-level confirm dialog. AlertDialog is the correct primitive here:
// this *is* an alert (destructive confirm). Lifted out of <StoreSheet />
// so nested dialogs (illegal Radix markup) are avoided.
export function DisconnectConfirmDialog({
  store,
  open,
  onOpenChange,
  onConfirm,
  pending,
}: DisconnectConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Desconectar {STORE_META[store].label}</AlertDialogTitle>
          <AlertDialogDescription>
            Se eliminarán todos los carritos de {STORE_META[store].label} guardados por semana. Tus
            listas, planes y productos vinculados no cambian.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            // AlertDialogAction accepts a Button variant in this project; pass
            // `destructive` instead of stacking color overrides.
            variant="destructive"
            onClick={(event) => {
              event.preventDefault();
              void onConfirm();
            }}
            disabled={pending}
          >
            {pending ? <ProgressDots aria-label="Desconectando" /> : "Sí, desconectar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AppAuthMissingSection({ onLogout }: { onLogout: () => void }) {
  return (
    <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
      <div className="flex items-start gap-2">
        <AlertCircleIcon className="mt-0.5 size-4 shrink-0" />
        <div>
          <p className="text-sm font-medium">Sesión expirada</p>
          <p className="mt-1 text-sm">
            Tu sesión de la app expiró. Cierra sesión y vuelve a entrar para continuar.
          </p>
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onLogout} className="border-red-300 dark:border-red-800">
        <LogOutIcon className="size-4" /> Cerrar sesión
      </Button>
    </div>
  );
}

function ConfiguredMissingSection({ storeLabel }: { storeLabel: string }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
      {storeLabel} aún no está disponible en esta versión de la app.
    </div>
  );
}

interface ConnectFormSectionProps {
  store: StoreId;
  email: string;
  password: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: () => Promise<void>;
  pending: boolean;
  error: string | null;
}

function ConnectFormSection({
  store,
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  pending,
  error,
}: ConnectFormSectionProps) {
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void onSubmit();
  }
  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <div>
        <p className="text-sm font-semibold">Conecta tu cuenta de {STORE_META[store].label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {store === "wong"
            ? "Usa el correo y contraseña de wong.pe. Después eliges una de tus direcciones guardadas en Wong."
            : "Solo guardamos lo necesario para armar tu carrito."}
        </p>
      </div>
      <div className="space-y-2">
        <Input
          id={`${store}-email`}
          name={`${store}-email`}
          type="email"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          placeholder={`Correo de ${STORE_META[store].label}`}
          autoComplete="email"
        />
        <Input
          id={`${store}-password`}
          name={`${store}-password`}
          type="password"
          value={password}
          onChange={(event) => onPasswordChange(event.target.value)}
          placeholder="Contraseña"
          autoComplete="current-password"
        />
      </div>
      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}
      <Button type="submit" className="w-full" disabled={pending || !email.trim() || !password}>
        {pending ? <ProgressDots aria-label="Conectando" /> : (
          <>
            <Link2Icon className="size-4" /> Conectar
          </>
        )}
      </Button>
    </form>
  );
}

function AccountBlock({
  email,
  pending,
  onRequestDisconnect,
}: {
  email: string | null;
  pending: boolean;
  onRequestDisconnect: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border bg-muted/20 p-3">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Cuenta conectada
        </p>
        <p className="mt-1 truncate text-sm font-medium">{email ?? "—"}</p>
        <p className="text-xs text-muted-foreground">
          Las compras se sumarán al carrito de esta cuenta.
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRequestDisconnect}
        disabled={pending}
        className="shrink-0"
      >
        <UnlinkIcon className="size-4" /> Desconectar
      </Button>
    </div>
  );
}

function AddressSection(props: StoreSheetProps) {
  const {
    addressLoading,
    addressError,
    addressMode,
    location,
    locationError,
    coverageDistricts,
    coverageLoading,
    filteredCoverageDistricts,
    districtAutocompleteOpen,
    highlightedDistrictIndex,
    selectedDistrictUbigeo,
    activeDistrictOptionId,
    geoResolving,
    saveAddressPending,
  } = props;

  const locationConfigured = Boolean(location?.configured);
  const showViewMode = addressMode === "view" && locationConfigured;

  return (
    <section className="space-y-3" aria-labelledby="plazavea-address-title">
      <h3 id="plazavea-address-title" className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Dirección de entrega
      </h3>
      {addressLoading ? (
        <div className="space-y-2">
          <div className="h-8 w-full animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-2/3 animate-pulse rounded-md bg-muted" />
        </div>
      ) : addressError ? (
        <div className="space-y-2">
          <p className="text-sm text-destructive">{addressError}</p>
          <Button variant="outline" size="sm" onClick={props.onRetryAddress}>
            <RefreshCwIcon className="size-4" /> Reintentar
          </Button>
        </div>
      ) : showViewMode ? (
        <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
          <MapPinIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-tight">
              {location?.neighborhood ?? "Dirección configurada"}
            </p>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {[location?.street, location?.number].filter(Boolean).join(" ") || "—"}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={props.onStartEditingAddress}>
            Editar
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="plazavea-location-district" className="text-xs">
              Distrito
            </Label>
            <div className="relative">
              <Input
                id="plazavea-location-district"
                name="plazavea-location-district"
                value={props.districtQuery}
                onChange={(event) => props.onDistrictQueryChange(event.target.value)}
                onFocus={props.onFocusDistrict}
                onBlur={props.onBlurDistrict}
                onKeyDown={props.onDistrictKeyDown}
                placeholder={coverageLoading ? "Cargando distritos…" : "Busca tu distrito"}
                autoComplete="off"
                disabled={coverageLoading || coverageDistricts.length === 0}
                role="combobox"
                aria-expanded={districtAutocompleteOpen}
                aria-controls="plazavea-location-district-options"
                aria-activedescendant={activeDistrictOptionId}
              />
              {districtAutocompleteOpen && filteredCoverageDistricts.length > 0 && (
                <div
                  id="plazavea-location-district-options"
                  role="listbox"
                  className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md"
                >
                  {filteredCoverageDistricts.map((district, index) => {
                    const highlighted =
                      highlightedDistrictIndex === index ||
                      (highlightedDistrictIndex === null && index === 0);
                    const isSelected = selectedDistrictUbigeo === district.ubigeo;
                    return (
                      <button
                        id={`plazavea-location-district-option-${district.ubigeo}`}
                        key={district.ubigeo}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => props.onSetHighlightedDistrictIndex(index)}
                        onClick={() => props.onSelectDistrict(district)}
                        className={cn(
                          "block w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted",
                          highlighted && "bg-muted",
                          isSelected && "font-medium",
                          focusRing,
                        )}
                      >
                        {[district.displayName, district.provinceName, district.departmentName]
                          .filter(Boolean)
                          .join(", ")}
                      </button>
                    );
                  })}
                </div>
              )}
              {districtAutocompleteOpen && props.districtQuery && filteredCoverageDistricts.length === 0 && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-2 text-sm text-muted-foreground shadow-md">
                  Sin distritos de PlazaVea aquí.
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-[1fr_5rem] gap-2">
            <Input
              id="plazavea-location-street"
              name="plazavea-location-street"
              value={props.street}
              onChange={(event) => props.onStreetChange(event.target.value)}
              placeholder="Calle / Avenida"
            />
            <Input
              id="plazavea-location-number"
              name="plazavea-location-number"
              value={props.number}
              onChange={(event) => props.onNumberChange(event.target.value)}
              placeholder="N°"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void props.onUseCurrentLocation()}
              disabled={geoResolving}
              className="gap-1.5"
            >
              <MapPinIcon className="size-4" />
              {geoResolving ? "Buscando…" : "Usar mi ubicación"}
            </Button>
            {locationConfigured && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={props.onCancelEditingAddress}
                disabled={saveAddressPending}
              >
                Cancelar
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              onClick={() => void props.onSaveAddress()}
              disabled={
                saveAddressPending ||
                coverageLoading ||
                !props.street.trim() ||
                !props.number.trim() ||
                !selectedDistrictUbigeo
              }
              className="ml-auto"
            >
              {saveAddressPending ? <ProgressDots aria-label="Guardando" /> : "Guardar"}
            </Button>
          </div>
          {!locationConfigured && (
            <p className="text-xs text-muted-foreground">
              PlazaVea exige una dirección y un distrito de cobertura antes de mostrar
              disponibilidad real.
            </p>
          )}
          {locationError && <p className="text-sm text-destructive">{locationError}</p>}
        </div>
      )}
    </section>
  );
}


// Wong owns the shopper's address book (wong.pe itself only lets a logged-in
// shopper choose among saved addresses), so instead of a typed address form we
// list those saved addresses. Their coordinates are Wong-verified, which is
// what resolves the delivering store, prices and stock.
function WongAddressSection(props: StoreSheetProps) {
  const {
    addressLoading,
    addressError,
    location,
    locationError,
    wongAddresses,
    selectingWongAddressId,
    refreshingWongAddresses,
  } = props;
  const addressesUrl = STORE_META.wong.addressesUrl!;
  const configured = Boolean(location?.configured);
  const selectedId = props.selectedWongAddressId;
  // Once an address is chosen, show just that one (with "Cambiar") instead of
  // the whole address book every time the sheet opens.
  const [changing, setChanging] = useState(false);
  useEffect(() => {
    setChanging(false);
  }, [selectedId]);
  const showList = !configured || changing;

  return (
    <section className="space-y-3" aria-labelledby="wong-address-title">
      <div className="flex items-center justify-between gap-2">
        <h3 id="wong-address-title" className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Dirección de entrega
        </h3>
        {showList && !addressLoading && !addressError && wongAddresses.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={props.onRefreshWongAddresses}
            disabled={refreshingWongAddresses}
            className="h-8 gap-1.5 text-xs text-muted-foreground"
          >
            <RefreshCwIcon className={cn("size-3.5", refreshingWongAddresses && "motion-safe:animate-spin")} />
            Actualizar
          </Button>
        )}
      </div>
      {addressLoading ? (
        <div className="space-y-2">
          <div className="h-14 w-full animate-pulse rounded-lg bg-muted" />
          <div className="h-14 w-full animate-pulse rounded-lg bg-muted" />
        </div>
      ) : addressError ? (
        <div className="space-y-2">
          <p className="text-sm text-destructive">{addressError}</p>
          <Button variant="outline" size="sm" onClick={props.onRetryAddress}>
            <RefreshCwIcon className="size-4" /> Reintentar
          </Button>
        </div>
      ) : !showList ? (
        <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
          <MapPinIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-tight">{location?.neighborhood ?? "Dirección configurada"}</p>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {[location?.street, location?.number].filter(Boolean).join(" ") || "—"}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setChanging(true)}>
            Cambiar
          </Button>
        </div>
      ) : wongAddresses.length === 0 ? (
        <div className="space-y-3 rounded-lg border border-dashed p-4">
          <p className="text-sm font-medium">No tienes direcciones guardadas en Wong</p>
          <p className="text-xs text-muted-foreground">
            Agrega tu dirección en wong.pe (Mi cuenta › Direcciones) y vuelve aquí. Así Wong confirma
            que llega a tu dirección y te muestra los precios y el stock de tu tienda.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={addressesUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLinkIcon className="size-4" /> Abrir wong.pe
              </a>
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={props.onRefreshWongAddresses}
              disabled={refreshingWongAddresses}
            >
              {refreshingWongAddresses ? <ProgressDots aria-label="Actualizando" /> : (
                <>
                  <RefreshCwIcon className="size-4" /> Actualizar
                </>
              )}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {!configured && (
            <p className="text-xs text-muted-foreground">
              Elige dónde entregar. Los precios y el stock dependen de la tienda Wong que atiende esa
              dirección.
            </p>
          )}
          <div role="radiogroup" aria-labelledby="wong-address-title" className="space-y-2">
            {wongAddresses.map((address) => {
              const selected = address.addressId === selectedId;
              const pending = selectingWongAddressId === address.addressId;
              return (
                <button
                  key={address.addressId}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => props.onSelectWongAddress(address.addressId)}
                  disabled={selectingWongAddressId !== null}
                  className={cn(
                    "flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                    selected ? "border-primary bg-primary/5" : "hover:bg-muted/50",
                    selectingWongAddressId !== null && !pending && "opacity-60",
                    focusRing,
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border",
                      selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
                    )}
                  >
                    {selected && <CheckIcon className="size-3.5" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-sm font-medium leading-tight">{address.neighborhood || "Dirección"}</span>
                      {address.selectedInWong && !configured && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          Última usada en Wong
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                      {[address.street, address.number].filter(Boolean).join(" ")}
                      {address.complement ? ` · ${address.complement}` : ""}
                    </span>
                  </span>
                  {pending && <ProgressDots aria-label="Guardando" />}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            ¿No ves tu dirección?{" "}
            <a
              href={addressesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn("font-medium text-foreground underline-offset-2 hover:underline", focusRing)}
            >
              Agrégala en wong.pe
            </a>{" "}
            y toca Actualizar.
          </p>
          {configured && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setChanging(false)}
              disabled={selectingWongAddressId !== null}
            >
              Cancelar
            </Button>
          )}
        </>
      )}
      {locationError && <p className="text-sm text-destructive">{locationError}</p>}
    </section>
  );
}
